import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Empty, Input, Modal, Spin, Tooltip, message as antMessage } from "antd";
import { Paperclip, X } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { useTranslation } from "react-i18next";
import MediaModal from "../../components/MediaModal";
import { mixGetV1 } from "../../services/mixin";
import LabelBar from "./LabelBar";

/** Bỏ dấu tiếng Việt, chữ thường: tìm mẫu tin nhắn nhanh không cần gõ dấu */
const foldVi = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase();

/** Nút icon ở ô soạn tin: là <button> thật (Tab / Enter dùng được), có viền khi focus bằng bàn phím */
const COMPOSER_ICON_BTN = "w-8 h-8 rounded-lg flex items-center justify-center text-000000e0 hover:text-[#3e91ab] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#3e91ab] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-000000e0 transition-colors";

/** Ô đính kèm chỉ nhận ảnh: luồng gửi hiện chỉ gửi tin dạng ảnh qua thư viện ảnh */
const ATTACH_ACCEPT = "image/*";

/** Gõ /1 khi còn /10../19 thì chờ chừng này (ms) xem có gõ tiếp số nữa không rồi mới bung */
const QUICK_EXPAND_DELAY = 500;

interface Props {
    /** Khách loại bình luận: chỉ trả lời bằng chữ, không đính kèm ảnh */
    isComment: boolean;
    careLabels: any[];
    activeLabels: string[];
    onToggleLabel: (key: string, id?: number) => void;
    /** Xem theo nhóm kênh: hiện tin trả lời sẽ đi qua kênh nào */
    isGroup?: boolean;
    replyChannelName?: string;
    channelTypeCode?: string;
    /** Tài khoản đang đăng nhập (tin gửi đi được ghi tên người này) */
    myName: string;
    myAvatar?: string;
    /** Gửi nội dung đang soạn; trả false nếu chưa gửi được thì giữ nguyên nội dung trong ô */
    onSend: (text: string, files: any[]) => boolean;
}

/**
 * Ô soạn tin của khung chat: thẻ chăm sóc, mẫu tin nhắn nhanh (gõ "/"), emoji, ảnh đính kèm.
 * Tự giữ state nội dung đang gõ nên gõ phím chỉ render lại ô này, không kéo theo cả khung chat.
 */
export default function Composer({
    isComment,
    careLabels,
    activeLabels,
    onToggleLabel,
    isGroup,
    replyChannelName,
    channelTypeCode,
    myName,
    myAvatar,
    onSend,
}: Props) {
    const { t } = useTranslation();
    const [message, setMessage] = useState("");
    const [files, setFiles] = useState<any[]>([]);
    const [openQuickMessage, setOpenQuickMessage] = useState(false);
    const [openEmoji, setOpenEmoji] = useState(false);
    const emojiRef = useRef<any>(null);
    // Thư viện ảnh (MediaModal)
    const [open, setOpen] = useState(false);

    // --- Mẫu tin nhắn nhanh ---
    const [quickMessageList, setQuickMessageList] = useState<{ id: number; title: string }[]>([]);
    const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);

    const fetchQuickMessages = useCallback(async (search?: string) => {
        setLoadingQuickMessages(true);
        try {
            const res = await mixGetV1('/api/v1/be/quick-messages', {
                is_active: 1,
                search: search || undefined,
                sort: 'position,asc',
            });
            if (res.status && res.data?.items) {
                setQuickMessageList(res.data.items);
            }
        } catch (e) {
            console.error('Failed to fetch quick messages:', e);
        }
        setLoadingQuickMessages(false);
    }, []);

    // Nạp sẵn mẫu tin nhắn nhanh để gõ "/" là dùng được ngay
    useEffect(() => {
        fetchQuickMessages();
    }, [fetchQuickMessages]);

    const [search, setSearch] = useState("");

    // Mẫu tin nhắn nhanh: "/12" là phím tắt theo số thứ tự; "/giá" thì lọc theo nội dung (không cần dấu).
    // Mỗi mẫu giữ số thứ tự gốc để phím tắt /số không đổi nghĩa khi đang lọc.
    const quickQuery = message.startsWith("/") && !/^\/\d*$/.test(message) ? foldVi(message.slice(1).trim()) : "";
    const filtered = useMemo(
        () => quickMessageList
            .map((item, i) => ({ ...item, number: i + 1 }))
            .filter((item) => !quickQuery || foldVi(item.title || "").includes(quickQuery)),
        [quickMessageList, quickQuery]
    );

    const [showQuickList, setShowQuickList] = useState(false);
    const [selectedQuickIndex, setSelectedQuickIndex] = useState(0);
    // Ref tới item đang chọn để cuộn theo khi di chuyển bằng phím lên/xuống
    const activeQuickItemRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (showQuickList) {
            activeQuickItemRef.current?.scrollIntoView({ block: "nearest" });
        }
    }, [selectedQuickIndex, showQuickList]);

    const quickExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Nội dung ô nhập mới nhất, để timer biết người dùng có sửa gì thêm không
    const messageRef = useRef(message);
    useEffect(() => { messageRef.current = message; }, [message]);

    const clearQuickExpandTimer = () => {
        if (quickExpandTimerRef.current) {
            clearTimeout(quickExpandTimerRef.current);
            quickExpandTimerRef.current = null;
        }
    };

    useEffect(() => clearQuickExpandTimer, []);

    // --- Emoji: bấm ra ngoài thì đóng ---
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (emojiRef.current && !emojiRef.current.contains(event.target)) {
                setOpenEmoji(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- Ảnh đính kèm ---
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Chỉ gửi được ảnh (xem ATTACH_ACCEPT): file khác báo ngay, không để tới lúc gửi mới lỗi
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files || []);
        // Xoá giá trị để chọn lại đúng file đó vẫn bắt được sự kiện change
        e.target.value = "";
        const images = picked.filter(f => f.type.startsWith("image/"));
        if (images.length < picked.length) {
            antMessage.warning(t("roomchat.attach_images_only"));
        }
        if (!images.length) return;
        setFiles(prev => [
            ...prev,
            ...images.map(file => ({ file, preview: URL.createObjectURL(file), type: file.type })),
        ]);
    };

    // --- Gửi ---
    // Có gì để gửi: bình luận chỉ trả lời bằng chữ, tin nhắn thì chữ hoặc ảnh
    const canSend = isComment ? !!message.trim() : (!!message.trim() || files.length > 0);

    const sendMessage = () => {
        if (!canSend) return;
        if (onSend(message.trim(), files)) {
            setMessage("");
            setFiles([]);
        }
    };

    return (
        <div className="border-t border-color-1f1f1f p-3 bg-white dark:bg-[#1f1f1f]">
            {files.length > 0 && (
                <div className="flex gap-2 mb-2 flex-wrap">

                    {files.map((item, index) => (

                        <div key={index} className="relative group">
                            {/* Nút xoá */}
                            <button
                                type="button"
                                aria-label={t("roomchat.remove_attachment")}
                                onClick={() => {
                                    setFiles(prev => prev.filter((_, i) => i !== index));
                                }}
                                className="absolute top-[-6px] right-[-6px] w-[18px] h-[18px] rounded-full bg-black/70 text-white text-[12px] flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition"
                            >
                                <X size={12} />
                            </button>
                            {item.type.startsWith("image") ? (

                                <img
                                    src={item.preview}
                                    className="w-[70px] h-[70px] object-cover rounded"
                                    alt="preview"
                                />

                            ) : (

                                <div className="px-3 py-2 bg-gray-100 rounded text-sm">
                                    {item.file.name}
                                </div>

                            )}

                        </div>

                    ))}

                </div>
            )}

            <LabelBar
                labels={careLabels}
                activeLabels={activeLabels}
                onToggle={onToggleLabel}
            />

            {/* Xem theo nhóm kênh: danh sách gộp nhiều kênh nên phải nói rõ
                tin trả lời sẽ đi qua kênh nào của khách */}
            {isGroup && replyChannelName && (
                <div className="mt-2 flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                    <span>{t("roomchat.reply_via_channel") || "Trả lời qua kênh"}:</span>
                    <span className="font-medium text-[#3e91ab] truncate max-w-[260px]" title={replyChannelName}>
                        {replyChannelName}
                    </span>
                    {channelTypeCode && (
                        <span className="text-[10px] uppercase px-1.5 py-[1px] rounded bg-gray-100 dark:bg-[#2a2a2a] text-gray-500">
                            {channelTypeCode}
                        </span>
                    )}
                </div>
            )}

            <div className="relative mt-2 flex flex-col items-end gap-2 border border-gray-300 rounded-lg px-[18px] py-[16px] bg-[#fff] dark:bg-[#262626]
            focus-within:border-[#3e91ab]
            focus-within:ring-1
            focus-within:ring-[#3e91ab]">
                {showQuickList && filtered.length > 0 && (
                    <div
                        className="
                            absolute
                            bottom-[60px]
                            left-0
                            right-0
                            max-h-[260px]
                            overflow-y-auto
                            bg-white
                            dark:bg-[#1f1f1f]
                            border
                            border-gray-200
                            dark:border-[#333]
                            rounded-lg
                            shadow-lg
                            z-50
                        "
                    >
                        {filtered.map((item, index) => (
                            <div
                                key={item.id}
                                role="option"
                                aria-selected={selectedQuickIndex === index}
                                ref={selectedQuickIndex === index ? activeQuickItemRef : null}
                                onClick={() => {
                                    setMessage(item.title);
                                    setShowQuickList(false);
                                }}
                                className={`
                                    px-3
                                    py-2
                                    cursor-pointer
                                    border-l-4
                                    flex
                                    gap-1
                                    items-center
                                    ${selectedQuickIndex === index
                                        ? "bg-[#3e91ab]/10 border-[#3e91ab] dark:bg-[#3e91ab]/20"
                                        : "border-transparent hover:bg-gray-50 dark:hover:bg-[#262626]"
                                    }
                                `}
                            >
                                <div className={`font-semibold text-[13px] ${selectedQuickIndex === index
                                    ? "text-[#2f6f83] dark:text-[#7cc3d8]"
                                    : "text-[#3e91ab] dark:text-[#5fb0c7]"
                                    }`}>
                                    /{item.number}
                                </div>

                                <div className={`text-[13px] line-clamp-2 ${selectedQuickIndex === index
                                    ? "text-gray-900 dark:text-white font-medium"
                                    : "text-gray-600 dark:text-gray-300"
                                    }`}>
                                    {item.title}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <textarea
                    value={message}
                    onChange={(e) => {
                        const value = e.target.value;

                        setMessage(value);

                        // Gõ thêm ký tự nào cũng huỷ lượt hẹn giờ đang chờ
                        clearQuickExpandTimer();

                        if (value.startsWith("/")) {

                            const match = value.match(/^\/(\d+)$/);

                            if (match) {
                                const num = Number(match[1]);
                                const idx = num - 1;
                                const item = filtered[idx];

                                if (item) {
                                    // Highlight sẵn item khớp
                                    setSelectedQuickIndex(idx);

                                    // Còn số dài hơn bắt đầu bằng số đang gõ không?
                                    // (vd đang gõ /1 nhưng còn /10 ... /19 hợp lệ)
                                    const hasLongerCandidate = num * 10 <= filtered.length;

                                    if (!hasLongerCandidate) {
                                        setMessage(item.title);
                                        setShowQuickList(false);
                                        return;
                                    }

                                    // Còn số dài hơn: chờ xem người dùng có gõ tiếp không
                                    setShowQuickList(true);

                                    quickExpandTimerRef.current = setTimeout(() => {
                                        quickExpandTimerRef.current = null;

                                        // Ô nhập đã đổi trong lúc chờ thì bỏ qua
                                        if (messageRef.current !== value) return;

                                        setMessage(item.title);
                                        setShowQuickList(false);
                                    }, QUICK_EXPAND_DELAY);

                                    return;
                                }
                            }

                            setSelectedQuickIndex(0);
                            setShowQuickList(true);
                        } else {
                            setShowQuickList(false);
                        }
                    }}
                    onKeyDown={(e) => {

                        if (showQuickList) {

                            if (e.key === "ArrowDown") {
                                e.preventDefault();
                                clearQuickExpandTimer();

                                setSelectedQuickIndex(prev =>
                                    Math.min(prev + 1, filtered.length - 1)
                                );

                                return;
                            }

                            if (e.key === "ArrowUp") {
                                e.preventDefault();
                                clearQuickExpandTimer();

                                setSelectedQuickIndex(prev =>
                                    Math.max(prev - 1, 0)
                                );

                                return;
                            }

                            if (e.key === "Enter") {
                                e.preventDefault();
                                clearQuickExpandTimer();

                                const selected = filtered[selectedQuickIndex];

                                if (selected) {
                                    setMessage(selected.title);
                                    setShowQuickList(false);
                                }

                                return;
                            }

                            if (e.key === "Escape") {
                                clearQuickExpandTimer();
                                setShowQuickList(false);
                                return;
                            }
                        }

                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    className="w-full resize-y min-h-[24px] outline-none bg-transparent text-sm text-000000e0 overflow-y-auto"
                    rows={1}
                    placeholder={t("roomchat.composer_placeholder")}
                    aria-label={t("roomchat.composer_placeholder")}
                />

                {openEmoji && (
                    <div
                        ref={emojiRef}
                        className="absolute bottom-[70px] right-[10px] z-50"
                    >
                        <EmojiPicker
                            onEmojiClick={(emojiData) => {
                                setMessage(prev => prev + emojiData.emoji);
                            }}
                        />
                    </div>
                )}

                <div className="w-full flex items-center justify-end gap-1 text-gray-400 text-lg">

                    {/* Nhiều nhân viên dùng chung hộp chat: cho biết tin sẽ được ghi tên ai */}
                    {myName && (
                        <div className="mr-auto min-w-0 flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                            <Avatar
                                size={20}
                                src={myAvatar || undefined}
                                className="flex-shrink-0 bg-[#3e91ab] text-white text-[10px]"
                            >
                                {myName.charAt(0).toUpperCase()}
                            </Avatar>
                            <span className="whitespace-nowrap">{t("roomchat.replying_as")}:</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200 truncate" title={myName}>
                                {myName}
                            </span>
                        </div>
                    )}

                    {!isComment && (
                        <Tooltip title={t("roomchat.add_image_tooltip")}>
                            <button type="button" aria-label={t("roomchat.add_image_tooltip")} onClick={() => setOpen(true)} className={COMPOSER_ICON_BTN}>
                                <svg viewBox="64 64 896 896" focusable="false" data-icon="picture" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 632H136v-39.9l138.5-164.3 150.1 178L658.1 489 888 761.6V792zm0-129.8L664.2 396.8c-3.2-3.8-9-3.8-12.2 0L424.6 666.4l-144-170.7c-3.2-3.8-9-3.8-12.2 0L136 652.7V232h752v430.2zM304 456a88 88 0 100-176 88 88 0 000 176zm0-116c15.5 0 28 12.5 28 28s-12.5 28-28 28-28-12.5-28-28 12.5-28 28-28z"></path></svg>
                            </button>
                        </Tooltip>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ATTACH_ACCEPT}
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                    />

                    <Tooltip title={t("roomchat.emoji_tooltip")}>
                        <button type="button" aria-label={t("roomchat.emoji_tooltip")} aria-expanded={openEmoji} onClick={() => setOpenEmoji(!openEmoji)} className={COMPOSER_ICON_BTN}>
                            <svg viewBox="64 64 896 896" focusable="false" data-icon="smile" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M288 421a48 48 0 1096 0 48 48 0 10-96 0zm352 0a48 48 0 1096 0 48 48 0 10-96 0zM512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm263 711c-34.2 34.2-74 61-118.3 79.8C611 874.2 562.3 884 512 884c-50.3 0-99-9.8-144.8-29.2A370.4 370.4 0 01248.9 775c-34.2-34.2-61-74-79.8-118.3C149.8 611 140 562.3 140 512s9.8-99 29.2-144.8A370.4 370.4 0 01249 248.9c34.2-34.2 74-61 118.3-79.8C413 149.8 461.7 140 512 140c50.3 0 99 9.8 144.8 29.2A370.4 370.4 0 01775.1 249c34.2 34.2 61 74 79.8 118.3C874.2 413 884 461.7 884 512s-9.8 99-29.2 144.8A368.89 368.89 0 01775 775zM664 533h-48.1c-4.2 0-7.8 3.2-8.1 7.4C604 589.9 562.5 629 512 629s-92.1-39.1-95.8-88.6c-.3-4.2-3.9-7.4-8.1-7.4H360a8 8 0 00-8 8.4c4.4 84.3 74.5 151.6 160 151.6s155.6-67.3 160-151.6a8 8 0 00-8-8.4z"></path></svg>
                        </button>
                    </Tooltip>

                    {/* <Tooltip title={t("roomchat.quick_message_tooltip")}>
                        <span
                            onClick={() => {
                                setOpenQuickMessage(true);
                                setSearch("");
                                fetchQuickMessages();
                            }}
                            className="cursor-pointer text-000000e0 hover:text-[#3e91ab]"
                        >
                            <svg viewBox="64 64 896 896" focusable="false" data-icon="thunderbolt" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M848 359.3H627.7L825.8 109c4.1-5.3.4-13-6.3-13H436c-2.8 0-5.5 1.5-6.9 4L170 547.5c-3.1 5.3.7 12 6.9 12h174.4l-89.4 357.6c-1.9 7.8 7.5 13.3 13.3 7.7L853.5 373c5.2-4.9 1.7-13.7-5.5-13.7zM378.2 732.5l60.3-241H281.1l189.6-327.4h224.6L487 427.4h211L378.2 732.5z"></path></svg>
                        </span>
                    </Tooltip> */}
                    {!isComment && (
                        <Tooltip title={t("roomchat.attach_image_tooltip")}>
                            <button
                                type="button"
                                aria-label={t("roomchat.attach_image_tooltip")}
                                onClick={handleUploadClick}
                                className={COMPOSER_ICON_BTN}
                            >
                                <Paperclip size={18} />
                            </button>
                        </Tooltip>
                    )}

                    <Tooltip title={t("roomchat.send_tooltip")}>
                        <button
                            type="button"
                            aria-label={t("roomchat.send_tooltip")}
                            onClick={sendMessage}
                            disabled={!canSend}
                            className={`${COMPOSER_ICON_BTN} ${canSend ? "!text-[#3e91ab]" : ""}`}
                        >
                            <svg viewBox="64 64 896 896" focusable="false" data-icon="send" width="1em" height="1em" fill="currentColor" aria-hidden="true"><defs><style></style></defs><path d="M931.4 498.9L94.9 79.5c-3.4-1.7-7.3-2.1-11-1.2a15.99 15.99 0 00-11.7 19.3l86.2 352.2c1.3 5.3 5.2 9.6 10.4 11.3l147.7 50.7-147.6 50.7c-5.2 1.8-9.1 6-10.3 11.3L72.2 926.5c-.9 3.7-.5 7.6 1.2 10.9 3.9 7.9 13.5 11.1 21.5 7.2l836.5-417c3.1-1.5 5.6-4.1 7.2-7.1 3.9-8 .7-17.6-7.2-21.6zM170.8 826.3l50.3-205.6 295.2-101.3c2.3-.8 4.2-2.6 5-5 1.4-4.2-.8-8.7-5-10.2L221.1 403 171 198.2l628 314.9-628.2 313.2z"></path></svg>
                        </button>
                    </Tooltip>

                </div>

            </div>

            <Modal
                open={openQuickMessage}
                onCancel={() => setOpenQuickMessage(false)}
                footer={null}
                width={520}
                title={t("roomchat.quick_message_title")}
            >
                <Input
                    placeholder={t("roomchat.search_quick_message")}
                    className="mb-4"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        fetchQuickMessages(e.target.value);
                    }}
                />

                <Spin spinning={loadingQuickMessages}>
                    <div className="max-h-[320px] overflow-y-auto space-y-2">
                        {filtered.length === 0 ? (
                            <div className="h-[220px] flex items-center justify-center">
                                <Empty description={t("roomchat.no_data")} />
                            </div>
                        ) : (
                            filtered.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        setMessage(item.title);
                                        setOpenQuickMessage(false);
                                    }}
                                    className="border rounded-lg p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f1f1f] transition"
                                >
                                    <div className="text-gray-500 text-[13px] text-000000e0 line-clamp-2">
                                        {item.title}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Spin>
            </Modal>

            <MediaModal
                open={open} onClose={() => setOpen(false)}
                onSelectImages={(imgs) => {
                    setFiles((prev) => [...prev, ...imgs]); // 🔥 add vào files
                }}
            />

        </div>
    );
}
